import {
  BorderOutlined,
  CaretRightOutlined,
  LoadingOutlined,
  PauseOutlined
} from '@ant-design/icons';
import { Button, InputNumber, Tooltip } from 'antd';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from './apiError';
import type { NoteElementSelection } from './App';
import { Playback, playScore, ScoreNote } from './audio/player';
import { getAPIURL } from './host';
import { clearSounding, followPlayback, Following, noteSpansOf } from './scoreFollowing';

/** The notes of the whole piece (backend api/analysis/performance.py). */
interface Performance {
  notes: ScoreNote[];
  /** Quarter notes per minute of the score's first metronome mark, if it has one. */
  tempo: number | null;
  end: number;
  /** Offset and number of each bar. */
  measures: [number, number][];
}

type Status = 'idle' | 'loading' | 'playing' | 'paused';

const defaultTempo = 80;

/** The number of the bar at the offset. */
const barAt = (measures: [number, number][], offset: number) => {
  let bar = measures[0]?.[1];
  for (const [start, number] of measures) {
    if (start > offset + 1e-6) break;
    bar = number;
  }

  return bar;
};

/**
 * Plays the whole piece on the piano, all voices, from the page on view (paper, 7.2: audio playback). While it plays,
 * the notes that sound light up in the sheet and the pages turn with it (`onPosition`).
 */
const ScorePlayer = ({
  musicSheetID,
  noteElements,
  onPosition,
  startOffset
}: {
  musicSheetID: string;
  noteElements: NoteElementSelection | undefined;
  /** Called with the position (quarter notes) while the piece plays. */
  onPosition: (offset: number) => void;
  /** Where playing starts: the beginning of the page on view. */
  startOffset: number;
}) => {
  const { t: translate } = useTranslation();
  const [performance, setPerformance] = useState<Performance>();
  const [loadError, setLoadError] = useState<string>();
  const [bpm, setBpm] = useState<number>();
  const [status, setStatus] = useState<Status>('idle');
  const [bar, setBar] = useState<number>();
  const playback = useRef<Playback>(undefined);
  const following = useRef<Following>(undefined);
  const pausedAt = useRef<number>(undefined);
  const latestOnPosition = useRef(onPosition);
  latestOnPosition.current = onPosition;
  // The written notes of the sheet, drawn anew when the sheet is zoomed: the following reads the latest ones.
  const spans = useMemo(() => noteSpansOf(noteElements), [noteElements]);
  const latestSpans = useRef(spans);
  latestSpans.current = spans;

  useEffect(() => {
    let isCurrent = true;
    axios
      .get<Performance>(`${getAPIURL()}/performance/${musicSheetID}`)
      .then(({ data }) => isCurrent && setPerformance(data))
      .catch((error) => isCurrent && setLoadError(apiErrorMessage(error)));

    return () => {
      isCurrent = false;
      following.current?.stop();
      if (playback.current) {
        const ending = playback.current;
        playback.current = undefined;
        ending.stop();
      }
    };
  }, [musicSheetID]);

  const tempo = bpm ?? performance?.tempo ?? defaultTempo;

  const start = (from: number, atTempo = tempo) => {
    if (!performance) return;
    const started = playScore(performance.notes, atTempo, from);
    playback.current = started;
    pausedAt.current = undefined;
    setStatus('loading');
    setBar(barAt(performance.measures, from));
    // The sounding notes light up; the bar and the page follow (the page only when it changes).
    following.current?.release();
    following.current = followPlayback(() => latestSpans.current, started, {
      onOffset: (offset) => {
        setBar(barAt(performance.measures, offset));
        latestOnPosition.current(offset);
      }
    });
    started.started.then(() => {
      if (playback.current === started) setStatus('playing');
    });
    started.finished.then(() => {
      // Ended by itself or by another playback (a family): not by pausing or stopping here.
      if (playback.current !== started) return;
      playback.current = undefined;
      setBar(undefined);
      setStatus('idle');
    });
  };

  const pause = () => {
    const pausing = playback.current;
    if (!pausing) return;
    pausedAt.current = pausing.position();
    playback.current = undefined;
    // The notes stay lit where the piece paused.
    following.current?.release();
    pausing.stop();
    setStatus('paused');
  };

  const stop = () => {
    const stopping = playback.current;
    playback.current = undefined;
    pausedAt.current = undefined;
    stopping?.stop();
    following.current?.stop();
    clearSounding();
    setBar(undefined);
    setStatus('idle');
  };

  const changeTempo = (value: number | null) => {
    if (value === null || value < 30 || value > 240) return;
    setBpm(value);
    // A playing piece goes on from where it is, at the new tempo.
    if (playback.current && (status === 'playing' || status === 'loading')) {
      const position = playback.current.position();
      const changing = playback.current;
      playback.current = undefined;
      following.current?.release();
      changing.stop();
      start(position, value);
    }
  };

  const playLabel = loadError
    ? translate('piece.failed', { reason: loadError })
    : status === 'playing' || status === 'loading'
      ? translate('piece.pause')
      : status === 'paused'
        ? translate('piece.resume')
        : startOffset > 0
          ? translate('piece.playFromPage')
          : translate('piece.play');

  return (
    <div aria-label={translate('piece.player')} className="flex items-center gap-1" role="group">
      <Tooltip title={playLabel}>
        <Button
          aria-label={playLabel}
          disabled={!performance}
          icon={
            status === 'loading' ? (
              <LoadingOutlined />
            ) : status === 'playing' ? (
              <PauseOutlined />
            ) : (
              <CaretRightOutlined />
            )
          }
          onClick={() => {
            if (status === 'playing' || status === 'loading') pause();
            else if (status === 'paused' && pausedAt.current !== undefined) start(pausedAt.current);
            else start(startOffset);
          }}
          type={status === 'playing' ? 'primary' : 'default'}
        />
      </Tooltip>
      <Tooltip title={translate('piece.stop')}>
        <Button
          aria-label={translate('piece.stop')}
          disabled={status === 'idle'}
          icon={<BorderOutlined />}
          onClick={stop}
        />
      </Tooltip>
      <Tooltip title={translate('piece.tempo')}>
        <InputNumber
          aria-label={translate('piece.tempo')}
          disabled={!performance}
          max={240}
          min={30}
          onChange={changeTempo}
          prefix="♩ ="
          step={4}
          style={{ width: 96 }}
          value={tempo}
        />
      </Tooltip>
      {bar !== undefined && (
        <span className="whitespace-nowrap px-1 text-sm tabular-nums text-slate-600">
          {translate('piece.bar', { bar })}
        </span>
      )}
    </div>
  );
};

export default ScorePlayer;
